import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  X,
  LayoutDashboard,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { DashboardService } from '../services';
import { calculatePercentage } from '../utils';
import type { CalibrationTrendData, SensorStatusData } from '../types';

const Dashboard: React.FC = () => {
  // Filter states
  const currentYear = new Date().getFullYear().toString();
  const [selectedYear] = useState(currentYear);
  const [selectedMonth] = useState('All Months');

  // Dashboard statistics
  const [dashboardStats, setDashboardStats] = useState({
    totalCalibrated: 0,
    totalPassed: 0,
    totalFailed: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch dashboard statistics when filters change
  useEffect(() => {
    const fetchStatistics = async () => {
      setIsLoading(true);
      try {
        const stats = await DashboardService.getStatistics(
          selectedYear,
          selectedMonth
        );
        setDashboardStats(stats);
      } catch (error) {
        console.error('Error loading dashboard statistics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatistics();
  }, [selectedYear, selectedMonth]);

  // Calibration trends (dynamic)
  const [calibrationTrends, setCalibrationTrends] = useState<CalibrationTrendData[]>([]);
  const [, setIsTrendsLoading] = useState(true);

  // Fetch trends when filters change
  useEffect(() => {
    const fetchTrends = async () => {
      setIsTrendsLoading(true);
      try {
        const trends = await DashboardService.getTrends(selectedYear, selectedMonth);
        setCalibrationTrends(trends);
      } catch (err) {
        console.error('Error loading calibration trends:', err);
      } finally {
        setIsTrendsLoading(false);
      }
    };

    fetchTrends();
  }, [selectedYear, selectedMonth]);

  // Calculate sensor status data based on actual stats
  const sensorStatusData: SensorStatusData[] = [
    { 
      name: 'Passed', 
      value: dashboardStats.totalPassed, 
      percentage: calculatePercentage(dashboardStats.totalPassed, dashboardStats.totalCalibrated), 
      color: '#10B981' 
    },
    { 
      name: 'Failed', 
      value: dashboardStats.totalFailed, 
      percentage: calculatePercentage(dashboardStats.totalFailed, dashboardStats.totalCalibrated), 
      color: '#EF4444' 
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header: icon + title */}
      <div className="flex items-start gap-3">
        <LayoutDashboard className="h-8 w-8 flex-shrink-0 text-primary-600 mt-0.5" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back! Here's what's happening with your calibration system.
          </p>
        </div>
      </div>

      {/* Filters */}
      {/* <div className="flex flex-col sm:flex-row gap-4 items-end">
        <div className="w-full sm:w-1/2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Year <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="input w-full h-10 text-sm px-4"
          >
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-1/2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Month
          </label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="input w-full h-10 text-sm px-4"
          >
            {months.map((month) => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>
        </div>
      </div> */}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* Total Calibrated Sensor */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-blue-100">
              <CheckCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">Total Calibrated Sensor</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {isLoading ? '...' : dashboardStats.totalCalibrated}
              </p>
            </div>
          </div>
        </div>

        {/* Total Passed Calibration */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">Total Passed Calibration</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {isLoading ? '...' : dashboardStats.totalPassed}
              </p>
            </div>
          </div>
        </div>

        {/* Total Failed Calibration */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-red-100">
              <X className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">Total Failed Calibration</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {isLoading ? '...' : dashboardStats.totalFailed}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Calibration Trends */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Calibration Trends</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={calibrationTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  fontSize={12}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  stroke="#9ca3af"
                />
                <YAxis 
                  fontSize={12}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  stroke="#9ca3af"
                  domain={[0, 40]}
                />
                <Tooltip 
                  contentStyle={{ 
                    fontSize: '12px',
                    padding: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }}
                />
                <Legend 
                  wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                  iconType="line"
                />
                <Line 
                  type="monotone" 
                  dataKey="Total Calibrations" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Passed" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Failed" 
                  stroke="#EF4444" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Sensor Status Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sensor Status Distribution</h3>
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sensorStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >
                  {sensorStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    fontSize: '12px',
                    padding: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }}
                  formatter={(value: number, name: string, props: any) => [
                    `${value} (${props.payload.percentage}%)`,
                    name
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {sensorStatusData.map((entry) => (
              <div key={entry.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-gray-700">{entry.name}</span>
                </div>
                <div className="text-gray-900 font-medium">
                  {entry.value} ({entry.percentage}%)
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

